import React, { useState, useEffect, useMemo, useRef } from "react";
import Swal from "sweetalert2";
import { 
  FaSearch, FaChevronDown, FaChevronUp, FaTrashAlt, FaMoneyBillWave, 
  FaPlus, FaTimes, FaEdit, FaPrint, FaLock, FaFileExport 
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { Helmet } from "react-helmet-async";
import axiosInstance from "../../api/axiosInstance";
import Pagination from "../Pagination/Pagination";
import "./AddDonation.css";

const DonorDropdown = ({ value, placeholder, donors, searchKey, onSelect, onClear, disabled }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlighted, setHighlighted] = useState(0);
  const ref = useRef(null);
  const listRef = useRef(null);

  const filtered = donors.filter((d) => (d[searchKey] || "").toLowerCase().includes(search.toLowerCase()));

  useEffect(() => { setHighlighted(0); }, [search, open]);
  
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open && listRef.current) {
      const el = listRef.current.querySelector('.admin-donation-drop-highlighted');
      if (el) el.scrollIntoView({ block: 'nearest' });
    }
  }, [highlighted, open]);

  const handleKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlighted((h) => Math.min(h + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlighted((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered.length > 0 && highlighted < filtered.length) { onSelect(filtered[highlighted]); setOpen(false); setSearch(""); }
    }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className="admin-donation-filter-wrap" ref={ref} style={{ opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
      <div className={`admin-donation-filter-trigger ${open ? "active" : ""}`} onClick={() => { if(!disabled) { setOpen((o) => !o); setSearch(""); } }}>
        <span className="admin-donation-filter-text" style={{ color: value ? "#2c3e50" : "#334155", fontWeight: value ? "600" : "400" }}>{value || placeholder}</span>
        <div className="admin-donation-trigger-icons">
          {value && !disabled && <FaTimes className="admin-donation-clear-icon" onClick={(e) => { e.stopPropagation(); onClear(); }} title="Clear Selection" />}
          <FaChevronDown size={10} style={{ color: "#64748b" }} />
        </div>
      </div>
      {open && (
        <div className="admin-donation-filter-menu" onClick={(e) => e.stopPropagation()}>
          <input type="text" placeholder={`Search ${placeholder.toLowerCase()}...`} value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={handleKey} autoFocus />
          <ul ref={listRef}>
            {filtered.length > 0 ? filtered.map((d, i) => (
              <li key={d._id} className={i === highlighted ? "admin-donation-drop-highlighted" : ""} onMouseEnter={() => setHighlighted(i)} onClick={() => { onSelect(d); setOpen(false); setSearch(""); }}>
                {d[searchKey]}
              </li>
            )) : <li className="admin-donation-drop-empty">No results found</li>}
          </ul>
        </div>
      )}
    </div>
  );
};

// ONLY Shiksha Nek and General Donation Options allowed here
const DONATION_TYPES = ["General Donation (सामान्य दान)", "Shiksha Nek (शिक्षा दान)"];
const MODES = ["Cash", "UPI", "Bank Transfer", "Cheque", "NEFT", "RTGS"];

const AddDonation = () => {
  const navigate = useNavigate();
  const [donors, setDonors] = useState([]);
  const [searchFilter, setSearchFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [modeFilter, setModeFilter] = useState("");
  const [dateFilter, setDateFilter] = useState(""); 

  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedDonor, setSelectedDonor] = useState(null);
  const [editDonationData, setEditDonationData] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [form, setForm] = useState({
    donationType: DONATION_TYPES[0], amount: "", date: new Date().toISOString().split("T")[0], mode: "Cash", refNo: ""
  });

  useEffect(() => { fetchDonors(); }, []);

  const fetchDonors = async () => {
    try {
      const res = await axiosInstance.get("/api/donors");
      setDonors(Array.isArray(res.data) ? res.data : res.data?.data || []);
    } catch (err) { console.error(err); }
  };

  const activeDonors = useMemo(() => donors.filter((d) => !d.isDeleted), [donors]);

  // Check for missing donor profile fields required for General/Shiksha Nek donations
  const getMissingFieldsForGeneral = (donor) => {
    if (!donor) return [];
    const missing = [];
    if (!donor.name?.toString().trim()) missing.push("Full Name");
    if (!donor.fatherName?.toString().trim()) missing.push("Father's Name");
    if (!donor.mobileNo?.toString().trim()) missing.push("Mobile Number");
    if (!donor.state?.toString().trim()) missing.push("State");
    if (!donor.district?.toString().trim()) missing.push("District");
    if (!donor.currentAddress?.toString().trim()) missing.push("Current Address");
    if (!donor.permanentAddress?.toString().trim()) missing.push("Permanent Address");
    return missing;
  };

  const missingProfileFields = useMemo(() => {
    return getMissingFieldsForGeneral(selectedDonor);
  }, [selectedDonor]);

  const allDonations = useMemo(() => {
    let list = [];
    donors.forEach((d) => {
      d.donations?.forEach((don) => {
        
        // HIDE ALL PROTSAHAN NIDHI DATA
        const dTypeLower = (don.donationType || "").toLowerCase();
        if (dTypeLower.includes("protsahan") || dTypeLower.includes("encouragement") || dTypeLower.includes("प्रोत्साहन")) {
          return; // Skip this donation entirely
        }

        if(don.txnStatus === "Success" || !don.txnStatus) {
          list.push({ ...don, donorId: d._id, memberId: d.memberId, name: d.name, mobileNo: d.mobileNo, isActive: don.isActive !== false });
        }
      });
    });

    // Apply Search Filters
    if (searchFilter) {
      const q = searchFilter.toLowerCase();
      list = list.filter((l) => (l.memberId || "").toLowerCase().includes(q) || (l.name || "").toLowerCase().includes(q) || (l.mobileNo || "").includes(q));
    }
    if (typeFilter) list = list.filter((l) => l.donationType === typeFilter);
    if (modeFilter) list = list.filter((l) => l.mode === modeFilter);
    
    if (dateFilter) {
      list = list.filter((l) => {
        const d = new Date(l.date);
        const localDateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        return localDateStr === dateFilter;
      });
    }

    return list.sort((a, b) => {
      const dateA = new Date(a.date).setHours(0,0,0,0);
      const dateB = new Date(b.date).setHours(0,0,0,0);
      
      if (dateB !== dateA) return dateB - dateA;
      return b._id.localeCompare(a._id);
    });
  }, [donors, searchFilter, typeFilter, modeFilter, dateFilter]);

  const paginatedDonations = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return allDonations.slice(startIndex, startIndex + itemsPerPage);
  }, [allDonations, currentPage, itemsPerPage]);

  const handleDonorSelect = (donor) => setSelectedDonor(donor);
  const handleClearDonor = () => {
    setSelectedDonor(null); setEditDonationData(null);
    setForm({ donationType: DONATION_TYPES[0], amount: "", date: new Date().toISOString().split("T")[0], mode: "Cash", refNo: "" });
  };

  const handleProcessPayment = async () => {
    if (!selectedDonor) return Swal.fire("Warning", "Please select a donor first.", "warning");
    
    // Strict block if chosen profile is incomplete
    if (missingProfileFields.length > 0) {
      return Swal.fire(
        "Incomplete Donor Profile",
        `This donor is missing the following required field(s): ${missingProfileFields.join(", ")}. Please edit the donor profile in Donor Management first before recording this donation.`,
        "warning"
      );
    }

    if (!form.amount || !form.date) return Swal.fire("Warning", "Amount and Date are required.", "warning");
    
    // Strict mandatory refNo validation for non-Cash transaction modes
    if (form.mode !== "Cash" && (!form.refNo || form.refNo.trim() === "")) {
      return Swal.fire("Warning", "Reference / Cheque Number is mandatory for all non-Cash payment modes.", "warning");
    }

    try {
      if (editDonationData) {
        await axiosInstance.put(`/api/donors/${editDonationData.donorId}/donate/${editDonationData.donationId}`, form);
        Swal.fire("Updated", "Donation updated successfully", "success");
      } else {
        await axiosInstance.post(`/api/donors/${selectedDonor._id}/donate`, form);
        Swal.fire("Success", "Donation added successfully", "success");
      }
      handleClearDonor(); setShowAddForm(false); fetchDonors();
    } catch (err) { Swal.fire("Error", "Failed to process donation", "error"); }
  };

  const handleEditDonation = (donation) => {
    const donor = donors.find((d) => d._id === donation.donorId);
    setSelectedDonor(donor);
    setForm({
      donationType: donation.donationType, amount: donation.amount,
      date: new Date(donation.date).toISOString().split("T")[0], mode: donation.mode, refNo: donation.refNo || ""
    });
    setEditDonationData({ donorId: donation.donorId, donationId: donation._id });
    setShowAddForm(true); window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteDonation = async (donorId, donationId) => {
    const res = await Swal.fire({ title: "Remove Donation?", text: "This action cannot be undone.", icon: "warning", showCancelButton: true, confirmButtonColor: "#e74c3c" });
    if (res.isConfirmed) {
      await axiosInstance.delete(`/api/donors/${donorId}/donate/${donationId}`);
      fetchDonors();
    }
  };

  const toggleDonationStatus = async (donorId, donationId, currentStatus) => {
    try {
      await axiosInstance.put(`/api/donors/${donorId}/donate/${donationId}/status`, { isActive: !currentStatus });
      fetchDonors();
    } catch (err) {
      Swal.fire("Error", "Could not update status", "error");
    }
  };

  const handleExportExcel = async () => {
    if (allDonations.length === 0) return Swal.fire('Notice', 'No data to export.', 'info');

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Donations Registry');

    worksheet.mergeCells('A1:J1');
    worksheet.getCell('A1').value = 'RAJPUT SHIKSHA KOSH';
    worksheet.getCell('A1').font = { name: 'Arial', size: 16, bold: true, color: { argb: 'FFB91C1C' } };
    worksheet.getCell('A1').alignment = { vertical: 'middle', horizontal: 'center' };

    worksheet.mergeCells('A2:J2');
    worksheet.getCell('A2').value = 'Donations Log & Statement (दान प्राप्ति विवरण सूची)';
    worksheet.getCell('A2').font = { name: 'Arial', size: 12, bold: true, color: { argb: 'FF2C3E50' } };
    worksheet.getCell('A2').alignment = { vertical: 'middle', horizontal: 'center' };

    worksheet.mergeCells('A3:J3');
    worksheet.getCell('A3').value = `Search: ${searchFilter || 'All'}  |  Date: ${dateFilter || 'All'}  |  Type: ${typeFilter || 'All'}  |  Mode: ${modeFilter || 'All'}`;
    worksheet.getCell('A3').font = { name: 'Arial', size: 10, italic: true, color: { argb: 'FF64748B' } };
    worksheet.getCell('A3').alignment = { vertical: 'middle', horizontal: 'center' };

    worksheet.addRow([]);

    const headers = [
      'S.No', 'Member ID', 'Date & Time', 'Donor Name', 'Contact Number', 
      'Donation Type', 'Payment Mode', 'Reference / Txn ID', 'Amount (₹)', 'Status'
    ];

    const headerRow = worksheet.addRow(headers);
    headerRow.eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF34495E' } };
      cell.font = { color: { argb: 'FFFFFFFF' }, bold: true, name: 'Arial', size: 10 };
      cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    });

    allDonations.forEach((don, i) => {
      const isPGTransaction = Boolean(don.merchantTxnNo) || Boolean(don.pgPaymentId) || Boolean(don.receiptNo && (don.receiptNo.includes('ONL') || don.receiptNo.includes('PUB')));
      const paymentModeDisplay = isPGTransaction ? 'Online Gateway' : (don.mode || 'CASH');
      const transactionDetails = don.refNo && don.refNo !== 'N/A' ? don.refNo : '—';
      
      const formattedDate = new Date(don.date).toLocaleDateString('en-IN') + 
        (/^[a-fA-F0-9]{24}$/.test(don._id) ? ' ' + new Date(parseInt(don._id.substring(0, 8), 16) * 1000).toLocaleTimeString("en-IN", { hour: '2-digit', minute: '2-digit' }) : '');

      const row = worksheet.addRow([
        i + 1,
        don.memberId || '—',
        formattedDate,
        don.name,
        don.mobileNo || '—',
        don.donationType,
        paymentModeDisplay,
        transactionDetails,
        don.amount,
        don.isActive ? 'Active' : 'Disabled'
      ]);

      row.eachCell((cell, colIndex) => {
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        cell.font = { name: 'Arial', size: 9 };
        
        if ([4, 6].includes(colIndex)) {
          cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
        } else if (colIndex === 9) {
          cell.alignment = { vertical: 'middle', horizontal: 'right', wrapText: true };
          cell.numFmt = '₹#,##0.00';
        } else {
          cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        }
      });

      const statusCell = row.getCell(10);
      statusCell.font = { bold: true, name: 'Arial', size: 9, color: { argb: don.isActive ? 'FF16A34A' : 'FF94A3B8' } };
    });

    worksheet.columns = [
      { width: 8 },   // S.No
      { width: 14 },  // Member ID
      { width: 22 },  // Date & Time
      { width: 22 },  // Donor Name
      { width: 15 },  // Contact Number
      { width: 25 },  // Donation Type
      { width: 18 },  // Payment Mode
      { width: 22 },  // Ref / Txn ID
      { width: 15 },  // Amount
      { width: 12 }   // Status
    ];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, 'Donations_Database.xlsx');
  };

  return (
    <>
    <Helmet>
                <title>Donations | Rajput Shiksha Kosh</title>
        
                <meta
                  name="description"
                  content="Manage Donations for the Rajput Shiksha Kosh organization."
                />
              </Helmet>
    <div className="admin-donation-container">
      <h2 className="admin-donation-title"><FaMoneyBillWave style={{ marginRight: "8px" }} /> Manage Donations</h2>

      <div className="admin-donation-controls">
        <div className="admin-donation-search-wrapper">
          <FaSearch className="admin-donation-search-icon" />
          <input type="text" className="admin-donation-search-input" placeholder="Search ID, Name, or Mobile..." value={searchFilter} onChange={(e) => { setSearchFilter(e.target.value); setCurrentPage(1); }} />
        </div>
        
        <div className="admin-donation-right-controls">
          <input 
            type="date" 
            value={dateFilter} 
            onChange={(e) => { setDateFilter(e.target.value); setCurrentPage(1); }} 
            className="admin-donation-items-select" 
            title="Filter by Specific Date" 
          />
          <select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setCurrentPage(1); }} className="admin-donation-items-select">
            <option value="">All Types</option>
            {DONATION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={modeFilter} onChange={(e) => { setModeFilter(e.target.value); setCurrentPage(1); }} className="admin-donation-items-select">
            <option value="">All Modes</option>
            {MODES.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <select value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} className="admin-donation-items-select">
            <option value="5">5 / page</option>
            <option value="10">10 / page</option>
            <option value="20">20 / page</option>
            <option value="50">50 / page</option>
          </select>
          <button className="admin-donation-btn admin-donation-btn-export" onClick={handleExportExcel} title="Export Filtered Excel">
            <FaFileExport /> Export Excel
          </button>
          <button className="admin-donation-btn admin-donation-btn-add" onClick={() => { setShowAddForm(!showAddForm); handleClearDonor(); }}>
            {showAddForm ? <><FaTimes /> Close Form</> : <><FaPlus /> Add Donation</>}
          </button>
        </div>
      </div>

      {showAddForm && (
        <div className="admin-donation-inline-form">
          <div className="admin-donation-form-header">
             <h3 className="admin-donation-form-title">{editDonationData ? "✏️ Edit Donation Record" : "📝 Record New Donation (Manual)"}</h3>
          </div>
          
          <h4 className="admin-donation-section-title">Step 1: Select Donor</h4>
          <div className="admin-donation-form-grid">
            <div className="admin-donation-form-group"><label>By Member ID</label><DonorDropdown value={selectedDonor?.memberId || ""} placeholder="Search Member ID" donors={activeDonors} searchKey="memberId" onSelect={handleDonorSelect} onClear={handleClearDonor} disabled={editDonationData}/></div>
            <div className="admin-donation-form-group"><label>By Name</label><DonorDropdown value={selectedDonor?.name || ""} placeholder="Search Name" donors={activeDonors} searchKey="name" onSelect={handleDonorSelect} onClear={handleClearDonor} disabled={editDonationData}/></div>
            <div className="admin-donation-form-group"><label>By Mobile No</label><DonorDropdown value={selectedDonor?.mobileNo || ""} placeholder="Search Mobile" donors={activeDonors} searchKey="mobileNo" onSelect={handleDonorSelect} onClear={handleClearDonor} disabled={editDonationData}/></div>
          
            {selectedDonor && (
              <div style={{
                gridColumn: "1 / -1",
                padding: "12px",
                borderRadius: "6px",
                background: missingProfileFields.length > 0 ? "#fff5f5" : "#f0fdf4",
                border: `1px solid ${missingProfileFields.length > 0 ? "#feb2b2" : "#bbf7d0"}`,
                marginTop: "10px",
                fontSize: "0.85rem"
              }}>
                {missingProfileFields.length > 0 ? (
                  <span style={{ color: "#c53030", fontWeight: "600" }}>
                    ⚠️ This profile cannot accept donations yet. Please update the donor profile in Donor Management with: {missingProfileFields.join(", ")}.
                  </span>
                ) : (
                  <span style={{ color: "#2f855a", fontWeight: "600" }}>
                    ✅ Selected Donor profile is fully complete. You can proceed with this donation.
                  </span>
                )}
              </div>
            )}
          </div>

          <h4 className="admin-donation-section-title">Step 2: Donation Details</h4>
          <div className="admin-donation-form-grid">
            <div className="admin-donation-form-group admin-donation-col-span-2">
              <label>Donation Type <span className="admin-donation-req">*</span></label>
              <div className="admin-donation-radios">
                {DONATION_TYPES.map((type) => (
                  <label key={type} className={`admin-donation-radio-label ${form.donationType === type ? "selected" : ""}`}>
                    <input type="radio" name="dType" value={type} checked={form.donationType === type} onChange={(e) => setForm({ ...form, donationType: e.target.value })} /> {type}
                  </label>
                ))}
              </div>
            </div>

            <div className="admin-donation-form-group">
              <label>Amount (₹) <span className="admin-donation-req">*</span></label>
              <input type="text" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value.replace(/\D/g, "") })} placeholder="Amount" />
            </div>

            <div className="admin-donation-form-group">
              <label>Date <span className="admin-donation-req">*</span></label>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>

            <div className="admin-donation-form-group">
              <label>Payment Mode <span className="admin-donation-req">*</span></label>
              <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })}>
                {MODES.map((m) => (<option key={m} value={m}>{m}</option>))}
              </select>
            </div>

            {form.mode !== "Cash" && (
              <div className="admin-donation-form-group">
                <label>Ref / Cheque No <span className="admin-donation-req">*</span></label>
                <input type="text" value={form.refNo} onChange={(e) => setForm({ ...form, refNo: e.target.value })} placeholder="Reference Details" />
              </div>
            )}
          </div>

          <div className="admin-donation-form-buttons">
            <button className="admin-donation-btn-cancel" onClick={() => setShowAddForm(false)}>Cancel</button>
            <button className="admin-donation-btn-confirm" onClick={handleProcessPayment}>{editDonationData ? "Update Donation" : "Submit Donation"}</button>
          </div>
        </div>
      )}

      <div className="admin-donation-table-wrapper">
        <table className="admin-donation-table">
          <thead>
            <tr>
              <th className="col-sno">S.No</th>
              <th className="col-memberid">Member ID</th>
              <th className="col-date">Date & Time</th>
              <th className="col-name admin-donation-align-left">Donor Name</th>
              <th className="col-type">Type</th>
              <th className="col-paymode">Payment Mode</th>
              <th className="col-amount">Amount</th>
              <th className="col-status">Status</th>
              <th className="col-action">Action</th>
            </tr>
          </thead>
          <tbody>
            {paginatedDonations.length === 0 ? (
                <tr><td colSpan="9" style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>No donation records found.</td></tr>
            ) : paginatedDonations.map((don, index) => {
              const isPGTransaction = Boolean(don.merchantTxnNo) || Boolean(don.pgPaymentId) || Boolean(don.receiptNo && (don.receiptNo.includes('ONL') || don.receiptNo.includes('PUB')));
              const serialNo = (currentPage - 1) * itemsPerPage + index + 1;

              return (
                <React.Fragment key={don._id}>
                  <tr className="admin-donation-main-row">
                    <td className="col-sno" style={{ fontWeight: "600", color: "#64748b" }}>{serialNo}</td>
                    
                    <td className="col-memberid" style={{ color: don.memberId ? "#334155" : "#e67e22", fontWeight: "700" }}>
                        {don.memberId || 'N/A'}
                    </td>
                    
                    <td className="col-date">
                      <div style={{ fontWeight: "600", color: "#334155" }}>{new Date(don.date).toLocaleDateString("en-IN")}</div>
                      <div style={{ fontSize: "0.75rem", color: "#64748b", marginTop: "2px" }}>
                        {/^[a-fA-F0-9]{24}$/.test(don._id) ? new Date(parseInt(don._id.substring(0, 8), 16) * 1000).toLocaleTimeString("en-IN", { hour: '2-digit', minute: '2-digit' }) : ""}
                      </div>
                    </td>
                    
                    <td className="col-name admin-donation-align-left">
                      <div className="admin-donation-name-wrapper" onClick={() => setExpandedRow(expandedRow === don._id ? null : don._id)}>
                        <div style={{display: 'flex', flexDirection: 'column'}}>
                           <span style={{ fontWeight: 600 }}>{don.name}</span>
                           <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{don.mobileNo}</span>
                        </div>
                        <button className="admin-donation-expand-btn">{expandedRow === don._id ? <FaChevronUp /> : <FaChevronDown />}</button>
                      </div>
                    </td>

                    <td className="col-type"><span className="admin-donation-badge-info">{don.donationType}</span></td>
                    <td className="col-paymode">
                      <div style={{ fontSize: "0.85rem", fontWeight: 600, color: '#334155' }}>
                        {isPGTransaction ? `🌐 Online Gateway` : `🏢 ${don.mode}`}
                      </div>
                    </td>
                    
                    <td className="col-amount" style={{fontWeight: 700, color: '#e67e22', fontSize: '1.05rem'}}>₹{don.amount}</td>
                    
                    <td className="col-status">
                      <label className="admin-donation-switch" title={don.isActive ? "Active" : "Inactive"}>
                        <input type="checkbox" checked={don.isActive} onChange={() => toggleDonationStatus(don.donorId, don._id, don.isActive)} />
                        <span className="admin-donation-slider round"></span>
                      </label>
                      <div style={{ fontSize: '0.75rem', marginTop: '4px', color: don.isActive ? '#e67e22' : '#94a3b8', fontWeight: '600' }}>{don.isActive ? 'Active' : 'Disabled'}</div>
                    </td>

                    <td className="col-action">
                      <div className="admin-donation-action-btns">
                        {isPGTransaction ? (
                            <>
                                <button className="admin-donation-btn-print" onClick={() => navigate("/admin/donation-receipt", { state: { donation: don } })} title="Print Receipt"><FaPrint /></button>
                                <span className="admin-donation-locked" title="Online Transactions Cannot be Edited"><FaLock /> Locked</span>
                            </>
                        ) : (
                            <>
                                <button className="admin-donation-btn-edit" onClick={() => handleEditDonation(don)} title="Edit"><FaEdit /></button>
                                <button className="admin-donation-btn-delete" onClick={() => deleteDonation(don.donorId, don._id)} title="Delete"><FaTrashAlt /></button>
                            </>
                        )}
                      </div>
                    </td>
                  </tr>

                  {expandedRow === don._id && (
                    <tr className="admin-donation-expanded-row">
                        <td colSpan="9">
                            <div className="admin-donation-expanded-content">
                                
                                {isPGTransaction && (
                                    <div className="admin-donation-exp-item exp-receipt">
                                       <span className="admin-donation-exp-label">Receipt Number</span>
                                       <span className="admin-donation-exp-value" style={{ 
                                            background: '#eff6ff', 
                                            color: '#3b82f6', 
                                            padding: '4px 8px', borderRadius: '4px', fontSize: '0.8rem', border: '1px solid #bfdbfe', display: 'inline-block', width: 'fit-content'
                                        }}>{don.receiptNo || 'N/A'}</span>
                                    </div>
                                )}

                                <div className="admin-donation-exp-item exp-memberid"><span className="admin-donation-exp-label">Member ID</span><span className="admin-donation-exp-value" style={{color: don.memberId ? '#334155' : '#e67e22'}}>{don.memberId || 'N/A'}</span></div>
                                <div className="admin-donation-exp-item exp-date">
                                    <span className="admin-donation-exp-label">Date & Time</span>
                                    <span className="admin-donation-exp-value">
                                        {new Date(don.date).toLocaleDateString("en-IN")}
                                        {/^[a-fA-F0-9]{24}$/.test(don._id) && ` at ${new Date(parseInt(don._id.substring(0, 8), 16) * 1000).toLocaleTimeString("en-IN", { hour: '2-digit', minute: '2-digit' })}`}
                                    </span>
                                </div>
                                <div className="admin-donation-exp-item exp-amount"><span className="admin-donation-exp-label">Amount</span><span className="admin-donation-exp-value" style={{fontWeight: 700, color: '#e67e22'}}>₹{don.amount}</span></div>
                                <div className="admin-donation-exp-item exp-type"><span className="admin-donation-exp-label">Donation Type</span><span className="admin-donation-exp-value">{don.donationType}</span></div>
                                <div className="admin-donation-exp-item exp-paymode"><span className="admin-donation-exp-label">Payment Mode</span><span className="admin-donation-exp-value">{isPGTransaction ? 'Online Gateway' : don.mode}</span></div>
                                
                                <div className="admin-donation-exp-item exp-status">
                                  <span className="admin-donation-exp-label">Status</span>
                                  <span className="admin-donation-exp-value" style={{color: don.isActive ? '#e67e22' : '#94a3b8', fontWeight: 'bold'}}>{don.isActive ? 'Active' : 'Disabled'}</span>
                                </div>

                                {isPGTransaction ? (
                                    <>
                                        <div className="admin-donation-exp-item exp-bank"><span className="admin-donation-exp-label">Bank Txn ID</span><span className="admin-donation-exp-value" style={{color: '#8b5cf6'}}>{don.refNo || 'N/A'}</span></div>
                                        <div className="admin-donation-exp-item exp-bank"><span className="admin-donation-exp-label">PG Method</span><span className="admin-donation-exp-value">{don.pgPaymentMode || 'N/A'}</span></div>
                                        <div className="admin-donation-exp-item exp-bank"><span className="admin-donation-exp-label">UPI / Card Info</span><span className="admin-donation-exp-value">{don.pgPaymentId || 'N/A'}</span></div>
                                        <div className="admin-donation-exp-item exp-bank"><span className="admin-donation-exp-label">Order Ref No</span><span className="admin-donation-exp-value">{don.merchantTxnNo || 'N/A'}</span></div>
                                    </>
                                ) : (
                                    <div className="admin-donation-exp-item exp-bank"><span className="admin-donation-exp-label">Cheque / Ref No</span><span className="admin-donation-exp-value">{don.refNo || 'N/A'}</span></div>
                                )}
                                
                                <div className="admin-donation-exp-item exp-action">
                                   <span className="admin-donation-exp-label">Actions</span>
                                   <div className="admin-donation-action-btns" style={{justifyContent: 'flex-start'}}>
                                        
                                        {isPGTransaction && (
                                            <button className="admin-donation-btn-print" onClick={() => navigate("/admin/donation-receipt", { state: { donation: don } })} title="Print Receipt"><FaPrint /> Print</button>
                                        )}
                                        
                                        {!isPGTransaction && (
                                            <>
                                                <button className="admin-donation-btn-edit" onClick={() => handleEditDonation(don)} title="Edit"><FaEdit /></button>
                                                <button className="admin-donation-btn-delete" onClick={() => deleteDonation(don.donorId, don._id)} title="Delete"><FaTrashAlt /></button>
                                            </>
                                        )}
                                   </div>
                                </div>

                            </div>
                        </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pagination totalItems={allDonations.length} itemsPerPage={itemsPerPage} currentPage={currentPage} onPageChange={setCurrentPage} />
    </div>
    </>
  );
};

export default AddDonation;